import React, { useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { formatPhoneNumberForInput } from '../utils/phoneUtils'

interface Member {
  userId: string
  email: string
  firstName: string
  lastName: string
  name: string
  phone?: string
  role: 'user' | 'admin'
  status: 'pending' | 'approved' | 'denied'
  isApproved: boolean
  joinedYear?: string
  createdAt: string
  updatedAt: string
  bio?: string
  avatar?: string
  emergencyContact?: {
    name: string
    phone: string
    relationship: string
  }
}

interface EditMemberModalProps {
  isOpen: boolean
  onClose: () => void
  member: Member | null
  onSave: (updatedMember: Member) => void
  isOwnProfile: boolean
}

const EditMemberModal: React.FC<EditMemberModalProps> = ({ 
  isOpen, 
  onClose, 
  member, 
  onSave, 
  isOwnProfile 
}) => {
  const { getAccessTokenSilently } = useAuth0()
  const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    bio: '',
    avatar: '',
    role: 'user' as 'user' | 'admin',
    status: 'approved' as 'pending' | 'approved' | 'denied',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: ''
  })
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string>('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Populate form when member changes
  useEffect(() => {
    if (member) {
      setFormData({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        phone: member.phone || '',
        bio: member.bio || '',
        avatar: member.avatar || '',
        role: member.role,
        status: member.status,
        emergencyContactName: member.emergencyContact?.name || '',
        emergencyContactPhone: member.emergencyContact?.phone || '',
        emergencyContactRelationship: member.emergencyContact?.relationship || ''
      })
      setAvatarPreview(member.avatar || '')
    }
  }, [member])

  const validate = () => {
    const newErrors: { [key: string]: string } = {}
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }
    if (formData.phone && !/^\d{3}-?\d{3}-?\d{4}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number'
    }

    // Emergency contact validation - if any field is filled, all should be filled
    const hasEmergencyContact = formData.emergencyContactName || formData.emergencyContactPhone || formData.emergencyContactRelationship
    if (hasEmergencyContact) {
      if (!formData.emergencyContactName.trim()) {
        newErrors.emergencyContactName = 'Emergency contact name is required'
      }
      if (!formData.emergencyContactPhone.trim()) {
        newErrors.emergencyContactPhone = 'Emergency contact phone is required'
      }
      if (!formData.emergencyContactRelationship.trim()) {
        newErrors.emergencyContactRelationship = 'Emergency contact relationship is required'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!member || !validate()) return

    const updatedMember: Member = {
      ...member,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
      phone: formData.phone.trim() || undefined,
      bio: formData.bio.trim() || undefined,
      avatar: avatarPreview || undefined,
      role: formData.role,
      status: formData.status,
      isApproved: formData.status === 'approved',
      emergencyContact: (formData.emergencyContactName && formData.emergencyContactPhone && formData.emergencyContactRelationship) ? {
        name: formData.emergencyContactName.trim(),
        phone: formData.emergencyContactPhone.trim(),
        relationship: formData.emergencyContactRelationship.trim()
      } : undefined,
      updatedAt: new Date().toISOString()
    }

    onSave(updatedMember)
    onClose()
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumberForInput(e.target.value)
    setFormData(prev => ({ ...prev, phone: formatted }))
  }

  const handleEmergencyPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumberForInput(e.target.value)
    setFormData(prev => ({ ...prev, emergencyContactPhone: formatted }))
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, avatar: 'Please select an image file' }))
      return
    }
    
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, avatar: 'Image must be less than 5MB' }))
      return
    }
    
    setAvatarFile(file)
    setErrors(prev => ({ ...prev, avatar: '' }))
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // Upload to S3
    try {
      setErrors(prev => ({ ...prev, avatar: 'Uploading...' }))
      
      // Get upload URL from backend
      const response = await fetch(`${API_BASE_URL}/me/avatar/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAccessTokenSilently()}`
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type
        })
      })

      if (!response.ok) {
        throw new Error('Failed to get upload URL')
      }

      const { uploadUrl, avatarUrl } = await response.json()

      // Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload avatar')
      }

      // Set the final avatar URL
      setFormData(prev => ({ ...prev, avatar: avatarUrl }))
      setAvatarPreview(avatarUrl)
      setErrors(prev => ({ ...prev, avatar: '' }))
      
    } catch (error) {
      console.error('Avatar upload error:', error)
      setErrors(prev => ({ ...prev, avatar: 'Failed to upload avatar. Please try again.' }))
      setAvatarFile(null)
      setAvatarPreview('')
    }
  }

  const removeAvatar = () => {
    setAvatarFile(null)
    setAvatarPreview('')
    setFormData(prev => ({ ...prev, avatar: '' }))
  }

  if (!member) return null

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-white flex justify-between items-center"
                >
                  {isOwnProfile ? 'Edit Your Profile' : `Edit ${member.name}'s Profile`}
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          First Name *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={formData.firstName}
                          onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        />
                        {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={formData.lastName}
                          onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        />
                        {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          placeholder="513-555-0123"
                        />
                        {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Avatar Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Profile Avatar
                    </label>
                    <div className="flex items-center space-x-4">
                      {/* Avatar Preview */}
                      <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        {avatarPreview ? (
                          <img 
                            src={avatarPreview} 
                            alt="Avatar preview" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-gray-400 text-2xl">
                            {formData.firstName.charAt(0).toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      
                      {/* Upload Controls */}
                      <div className="flex-1">
                        <input
                          type="file"
                          id="avatar"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                        <div className="flex space-x-2">
                          <label
                            htmlFor="avatar"
                            className="cursor-pointer bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors text-sm"
                          >
                            Choose Photo
                          </label>
                          {avatarPreview && (
                            <button
                              type="button"
                              onClick={removeAvatar}
                              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          JPG, PNG up to 5MB
                        </p>
                        {errors.avatar && <p className="mt-1 text-sm text-red-600">{errors.avatar}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Bio
                    </label>
                    <textarea
                      id="bio"
                      rows={4}
                      className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      value={formData.bio}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Tell us about yourself and your interest in horse racing..."
                    />
                  </div>

                  {/* Admin-only fields */}
                  {!isOwnProfile && (
                    <div>
                      <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Admin Settings</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Role
                          </label>
                          <select
                            id="role"
                            className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value={formData.role}
                            onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'user' | 'admin' }))}
                          >
                            <option value="user">Member</option>
                            <option value="admin">Administrator</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Status
                          </label>
                          <select
                            id="status"
                            className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value={formData.status}
                            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'pending' | 'approved' | 'denied' }))}
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="denied">Denied</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Emergency Contact */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Emergency Contact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="emergencyContactName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Name
                        </label>
                        <input
                          type="text"
                          id="emergencyContactName"
                          className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={formData.emergencyContactName}
                          onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
                        />
                        {errors.emergencyContactName && <p className="mt-1 text-sm text-red-600">{errors.emergencyContactName}</p>}
                      </div>
                      <div>
                        <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Phone
                        </label>
                        <input
                          type="tel"
                          id="emergencyContactPhone"
                          className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={formData.emergencyContactPhone}
                          onChange={handleEmergencyPhoneChange}
                          placeholder="513-555-0123"
                        />
                        {errors.emergencyContactPhone && <p className="mt-1 text-sm text-red-600">{errors.emergencyContactPhone}</p>}
                      </div>
                      <div>
                        <label htmlFor="emergencyContactRelationship" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Relationship
                        </label>
                        <input
                          type="text"
                          id="emergencyContactRelationship"
                          className="input-field mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={formData.emergencyContactRelationship}
                          onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactRelationship: e.target.value }))}
                          placeholder="Spouse, Parent, etc."
                        />
                        {errors.emergencyContactRelationship && <p className="mt-1 text-sm text-red-600">{errors.emergencyContactRelationship}</p>}
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Fill in all emergency contact fields or leave them all empty.
                    </p>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      className="btn-secondary dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default EditMemberModal
